import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getLatestArticles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 rounded-full bg-accent-100">
              <i className="ri-emotion-sad-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-3">文章未找到</h2>
            <p className="text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getLatestArticles(12)
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const others = getLatestArticles(12).filter(
      (a) => a.slug !== article.slug && a.category !== article.category
    );
    while (relatedArticles.length < 3 && others.length > 0) {
      const next = others.shift();
      if (next && !relatedArticles.find((r) => r.slug === next.slug)) {
        relatedArticles.push(next);
      }
    }
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '大鸡巴品种科普',
    },
    publisher: {
      '@type': 'Organization',
      name: '大鸡巴品种科普',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500">·</span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 大鸡巴品种科普`}
                className="w-full rounded-lg"
              />
            </div>

            <div
              className="prose max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-10">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <section className="border-t border-background-200 pt-10">
                <h3 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                  相关文章推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.slug} article={related} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}